import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { IGV } from "./config";

const CLAVE = "acerolima.carrito.v1";

const CarritoContext = createContext(null);

function leerDeDisco() {
  try {
    const crudo = localStorage.getItem(CLAVE);
    const items = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function reducer(items, accion) {
  switch (accion.tipo) {
    case "agregar": {
      const { producto, cantidad = 1 } = accion;
      const existente = items.find((i) => i.id === producto.id);
      if (existente) {
        return items.map((i) =>
          i.id === producto.id ? { ...i, cantidad: i.cantidad + cantidad } : i
        );
      }
      return [
        ...items,
        {
          id: producto.id,
          sku: producto.sku,
          nombre: producto.nombre,
          precio: producto.precio ?? null,
          unidad: producto.unidad || "und",
          categoria: producto.categoria,
          pesoUnitario: producto.especificaciones?.pesoUnitario ?? null,
          cantidad,
        },
      ];
    }

    case "cantidad": {
      const cantidad = Math.max(1, Math.floor(Number(accion.cantidad) || 1));
      return items.map((i) =>
        i.id === accion.id ? { ...i, cantidad } : i
      );
    }

    case "quitar":
      return items.filter((i) => i.id !== accion.id);

    case "vaciar":
      return [];

    default:
      return items;
  }
}

export function CarritoProvider({ children }) {
  const [items, dispatch] = useReducer(reducer, undefined, leerDeDisco);

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE, JSON.stringify(items));
    } catch {
      /* modo privado o almacenamiento lleno: el carrito vive solo en memoria */
    }
  }, [items]);

  const valor = useMemo(() => {
    // Los items sin precio cargado no suman: se resuelven por cotizacion.
    const subtotal = items.reduce(
      (acc, i) => acc + (i.precio ?? 0) * i.cantidad,
      0
    );
    const igv = subtotal * IGV;
    const pesoTotal = items.reduce(
      (acc, i) => acc + (i.pesoUnitario ?? 0) * i.cantidad,
      0
    );

    return {
      items,
      unidades: items.reduce((acc, i) => acc + i.cantidad, 0),
      lineas: items.length,
      subtotal,
      igv,
      total: subtotal + igv,
      pesoTotal,
      hayItemsSinPrecio: items.some((i) => i.precio === null),
      agregar: (producto, cantidad) =>
        dispatch({ tipo: "agregar", producto, cantidad }),
      cambiarCantidad: (id, cantidad) =>
        dispatch({ tipo: "cantidad", id, cantidad }),
      quitar: (id) => dispatch({ tipo: "quitar", id }),
      vaciar: () => dispatch({ tipo: "vaciar" }),
    };
  }, [items]);

  return (
    <CarritoContext.Provider value={valor}>{children}</CarritoContext.Provider>
  );
}

export function useCarrito() {
  const ctx = useContext(CarritoContext);
  if (!ctx) {
    throw new Error("useCarrito debe usarse dentro de <CarritoProvider>");
  }
  return ctx;
}
